import { useCallback, useSyncExternalStore } from 'react'

/**
 * The screens, named as `design/Gateway.dc.html` names them, in its order.
 *
 * `opposition` is the one tab the mockup does not name, and it is here rather
 * than folded into New case because it is a different door: an opposition has
 * a second party, a hard conflict gate before anything is written, and a
 * deadline series instead of a date. Putting it behind the same form would
 * mean either a form carrying fields most matters must ignore, or a wizard
 * that branches on its first question — and the branch would come *before*
 * the conflict check, which is the one thing that has to run first.
 *
 * All of them now have content. `phase` marks whether `screens/registry.tsx`'s
 * `SCREENS` map mounts a real screen for this tab (`'P2'`) or falls through
 * to the placeholder (anything else) — not, any more, a literal record of
 * the plan's own P0–P9 numbering: Workbench is chunk 7 of the published
 * Build Docket and P5 in `docs/IMPLEMENTATION_PLAN.md`, Intelligence is chunk 9
 * and P7, and both are marked `'P2'` here regardless, the moment they
 * actually mount. `blurb` is only ever rendered by the placeholder
 * (`App.tsx`'s `PlaceholderScreen`) — once a tab is mounted its `blurb` is
 * unused, kept here as documentation rather than deleted.
 */
export type TabKey =
  | 'new'
  | 'opposition'
  | 'queue'
  | 'case'
  | 'intelligence'
  | 'workbench'
  | 'guide'
  | 'courses'
  | 'admin'

export interface TabDef {
  key: TabKey
  /** The header label. The mockup's copy, verbatim. */
  label: string
  path: string
  /** The phase that builds this screen (docs/IMPLEMENTATION_PLAN.md §4). */
  phase: string
  /** What will be here, so an empty screen is still informative. */
  blurb: string
  /** Hidden entirely from non-admins — not shown-and-refused. */
  adminOnly?: boolean
}

/**
 * Pages that are real, reachable, and not part of this application.
 *
 * Currently empty. The law corpus explorer (`GET /api/corpus/law/explorer` —
 * `server/app/routers/corpus_law.py`) was the one entry here, server-rendered
 * HTML linked from the header because nothing else pointed at it — but
 * Intelligence's Corpus workspace now reads the same provision a reading
 * pane in the shell, calling the JSON route (`GET /api/corpus/law/provision`)
 * the explorer was itself built over, rather than sending an analyst out of
 * the application to read one. `IntelligenceScreen.tsx`'s own module
 * docstring, "The law reading pane is now here, not a new tab", carries the
 * detail; the explorer route itself still exists server-side (untouched,
 * still tested) for anyone who reaches it directly, it is simply no longer
 * the way this application links to it.
 *
 * The mechanism is left in place rather than deleted: a genuinely
 * server-rendered page with no React equivalent is a real, recurring shape
 * — this is where the next one goes, not something to rebuild from scratch.
 *
 * It is deliberately not a {@link TabDef}. A tab is an SPA route this
 * application mounts a screen for, and `App.mounting.test.tsx` asserts exactly
 * that correspondence; adding a fake route here to get a link in the header
 * would break the one guard that catches a screen built and never mounted.
 * Re-implementing a page in React instead of linking to it is a second UI
 * over one API, free to disagree with the first — the reason to add an entry
 * here rather than a screen for a future page like this one.
 *
 * `href` is built from `API_BASE` at render time rather than written as a
 * relative path, because the API may not be on the origin serving this
 * bundle. Every entry opens in a new tab: a page here is something you
 * consult *while* using this shell, and has no navigation back into it.
 */
export interface ExternalLink {
  label: string
  /** Appended to `API_BASE`. Leading slash required. */
  path: string
  /** The `title` attribute — says where the link goes before it is clicked. */
  hint: string
}

export const EXTERNAL_LINKS: readonly ExternalLink[] = []

export const TABS: readonly TabDef[] = [
  {
    key: 'new',
    label: 'New case',
    path: '/new',
    phase: 'P2',
    blurb:
      'The manual door: paste an office action, supply the grounds and dates extraction would have read, and run the stages.',
  },
  {
    key: 'opposition',
    label: 'Opposition',
    path: '/opposition',
    phase: 'P2',
    blurb:
      "The applicant's first response to a notice of opposition: the second party and its conflict check, the counterstatement deadline and the series that runs from it. Evidence rounds, hearings and extensions are out of scope.",
  },
  {
    key: 'queue',
    label: 'Queue',
    path: '/queue',
    phase: 'P2',
    blurb:
      "Ready and flagged lanes, SLA clocks counting to the verified deadline, business sign-off, batch approval, and today's digest.",
  },
  {
    key: 'case',
    label: 'Case',
    path: '/case',
    phase: 'P2',
    blurb:
      'One run, readable top to bottom: every stage and what it decided, both deadline dates, what retrieval found, the gate findings, and the draft when there is one.',
  },
  {
    key: 'intelligence',
    label: 'Intelligence',
    path: '/intelligence',
    phase: 'P2',
    blurb:
      'Corpus search over 34,206 sources, the research ask where every claim carries a chunk citation, outcome statistics, and the per-matter citation ledger.',
  },
  {
    key: 'workbench',
    label: 'Workbench',
    path: '/workbench',
    phase: 'P2',
    blurb:
      'One signed run at a time: the worklist as the working panel, the locked draft, sign-off that re-verifies the deadline, and export that re-checks the gate and renders the letterhead docx.',
  },
  {
    // Last of the working tabs, and deliberately: it is read once or twice and
    // then rarely, unlike the six before it. It is not admin-only — the limits
    // and the attorney's own responsibilities are exactly what a new user needs
    // on their first day, and putting them behind a role would hide them from
    // everybody who does the drafting.
    key: 'guide',
    label: 'User guide',
    path: '/guide',
    phase: 'P2',
    blurb:
      'What the platform does, the three pipelines it runs, what each check means, what stays the attorney’s own responsibility, and where it is honestly known to be weak.',
  },
  {
    // Next to the guide, for the same reason: it is for the first week and for
    // whoever joins later, and it is open to every signed-in role. The lessons
    // are a folder on the server (`TMVERSE_COURSES_DIR`); with none set the tab
    // says so rather than disappearing, because a missing tab reads as "this
    // platform has no training", and an empty one reads as "ask your admin".
    key: 'courses',
    label: 'Courses',
    path: '/courses',
    phase: 'P2',
    blurb:
      'Short recorded lessons on using the platform, from reading an office action to signing the letter, with captions, chapters and a searchable transcript.',
  },
  {
    key: 'admin',
    label: 'Admin',
    path: '/admin',
    phase: 'P0',
    blurb: 'Models & keys, Users & access, rate cards and settings.',
    adminOnly: true,
  },
] as const

/**
 * Where a bare URL, and the first screen after sign-in, lands: the Queue.
 *
 * Every role that can sign in carries `matters:read`, which is all the queue
 * routes ask for, so none of them lands on a permission error. The queue is
 * the day's inbox; New case stays one click away, and a blank form is a poor
 * first sight for someone who came back to work on matters already open.
 */
export const DEFAULT_PATH = '/queue'

/** The tabs a role may see. Admin is hidden, not merely refused. */
export function visibleTabs(isAdmin: boolean): readonly TabDef[] {
  return isAdmin ? TABS : TABS.filter((tab) => !tab.adminOnly)
}

/** Which tab a path belongs to, matching on the first segment so `/admin/users` counts. */
export function tabForPath(path: string): TabDef | undefined {
  const segment = `/${path.replace(/^\/+/, '').split('/')[0] ?? ''}`
  return TABS.find((tab) => tab.path === segment)
}

// ---------------------------------------------------------------------------
// Hash routing
// ---------------------------------------------------------------------------

/**
 * Hash routing rather than a router dependency.
 *
 * The app is a six-tab shell, not a deep navigation tree, and `#/admin/users`
 * is a real, shareable, reload-safe URL that needs no server rewrite rule and
 * no history-API fallback in whatever ends up serving `dist/`. If a later
 * phase needs nested routes with params, that is the moment to add a router —
 * not now, on the strength of six links.
 */
function currentPath(): string {
  const raw = window.location.hash.replace(/^#/, '')
  if (!raw || raw === '/') return DEFAULT_PATH
  return raw.startsWith('/') ? raw : `/${raw}`
}

function subscribe(onChange: () => void): () => void {
  window.addEventListener('hashchange', onChange)
  return () => window.removeEventListener('hashchange', onChange)
}

export interface Router {
  path: string
  navigate: (path: string) => void
  /** `href` for a real anchor, so middle-click and "open in new tab" work. */
  href: (path: string) => string
}

export function useRouter(): Router {
  const path = useSyncExternalStore(subscribe, currentPath, () => DEFAULT_PATH)

  const navigate = useCallback((next: string) => {
    const target = next.startsWith('/') ? next : `/${next}`
    if (currentPath() === target) return
    window.location.hash = target
  }, [])

  const href = useCallback((next: string) => `#${next.startsWith('/') ? next : `/${next}`}`, [])

  return { path, navigate, href }
}
